import {
    Component,
    CUSTOM_ELEMENTS_SCHEMA,
    NgZone,
    OnChanges,
    OnInit,
    SimpleChanges,
    ViewEncapsulation,
    inject,
    input,
    output,
    signal,
} from '@angular/core';
import { PropertyApiService } from 'apps/account/src/account-shared/services/property-api.service';
import { ParseService } from 'apps/account/src/account-shared/services/parse.service';
import { IdentityService } from 'account-hybrid/core/authentication';
import { endpointUrl } from 'apps/account/src/account-shared/services/endpoint-url.util';
import { languageHelper } from 'apps/account/src/account-shared/services/language-helper.util';
import { touchpointActivator } from 'apps/account/src/account-shared/services/touchpoint-activator.util';
import { TouchpointV1ApiService } from 'apps/account/src/account-shared/services/touchpoint-v1-api.service';
import { PlatformV1ApiService } from 'apps/account/src/account-shared/services/platform-v1-api.service';
import { MessagingV1ApiService } from 'apps/account/src/account-shared/services/messaging-v1-api.service';
import { StylingV1ApiService } from 'apps/account/src/account-shared/services/styling-v1-api.service';
import { ImageV1ApiService } from 'apps/account/src/account-shared/services/image-v1-api.service';
import { LegacyShimsModule } from '../../../../account-shared/legacy-shims/legacy-shims.module';
import { FtModalComponent } from '../../../common/components/modal/ft-modal.component';
import { FtEnsureModalSingleScrollDirective } from '../../stories/story-modal/ensure-modal-single-scroll.directive';
import { TranslateService } from '@ngx-translate/core';
import { CapitalizePipe } from '../contest-sheets/contest-sheets.pipes';
import { FtTouchpointMessagingComponent } from './ft-touchpoint-messaging.component';
import { FtTouchpointStylingComponent } from './ft-touchpoint-styling.component';
import { FtTouchpointIntegrationComponent } from './ft-touchpoint-integration.component';
import { FtTouchpointCertificateEmailComponent } from './ft-touchpoint-certificate-email.component';
import { FtTouchpointCertificateReminderEmailComponent } from './ft-touchpoint-certificate-reminder-email.component';
import { getWarmLeadNurturingControllerInjectables } from 'v2/app/components/v1-adapters/touchpoint-container/touchpointController';
import { resolveLayer } from 'v2/app/components/v1-adapters/touchpoint-container/touchpointTemplate';
import { SUPPORTED_TABS } from 'v2/app/components/v1-adapters/touchpoint-container/touchpoints';

// WS4 inc 6: touchpoints whose MESSAGING / STYLING / INTEGRATION editors are now
// the native FtTouchpoint{Messaging,Styling,Integration}Component. Mirror of the
// arrays in the (now-superseded) ftTouchpointContainer v1-adapter — the native
// container picks the right ngSwitch branch per (touchpoint, dataType); anything
// not listed falls through to the v1 fallback host (only contestPage-integration
// is reachable + not yet ported native).
const NATIVE_MESSAGING_TOUCHPOINTS = [
    'confirmation', 'followup', 'confirmation-email-image', 'invitation-email',
    'reminder-emails', 'reengagement-emails', 'poststay-email-image',
    'finalistsEmail', 'reminderEmail', 'activityEmail', 'winnerEmail',
    'landing', 'mobile', 'homepageEmail', 'photo-river', 'homepage', 'quote',
];
const NATIVE_STYLING_TOUCHPOINTS = [
    'contestPage',
    'confirmation', 'followup', 'pre-arrival-email', 'confirmation-email-image', 'invitation-email',
    'reminder-emails', 'reengagement-emails', 'poststay-email-image',
    'finalistsEmail', 'reminderEmail', 'activityEmail', 'winnerEmail',
    'landing', 'mobile', 'homepageEmail', 'photo-river', 'list', 'homepage', 'quote',
];
const NATIVE_INTEGRATION_TOUCHPOINTS = [
    'confirmation', 'landing', 'photo-river', 'list', 'homepage', 'quote',
    // WS5 Phase 3 #6: contestPage "Story Explorer" integration ported native (the last
    // reachable non-native tab) → the touchpoint container's v1 `mountV1Tab` $compile
    // fallback is now fully dead and removed below.
    'contestPage',
];

const WARM_LEAD_TOUCHPOINTS = ['certificate-email', 'certificate-reminder-email'];

/**
 * v1 `touchPointName.filter.js` namesByType — the modal-header title table. Each label is
 * `translate(key)`; the two certificate entries are literal (untranslated) strings, as in v1.
 */
const TOUCHPOINT_NAME_ENTRIES: ReadonlyArray<{ type: string; key?: string; literal?: string }> = [
    { type: 'confirmation', key: 'flipto.account.platform.preStay.touchpoints.ConfirmationPage' },
    { type: 'landing', key: 'flipto.account.platform.preStay.touchpoints.LandingPage' },
    { type: 'mobile', key: 'flipto.account.platform.preStay.touchpoints.MobileLandingPage' },
    { type: 'followup', key: 'flipto.account.platform.preStay.touchpoints.FollowupPage' },
    { type: 'confirmation-email-image', key: 'flipto.account.ConfirmationEmailBanner' },
    { type: 'invitation-email', key: 'flipto.account.InvitationEmail' },
    { type: 'quote', key: 'flipto.account.platform.postStay.touchpoints.PostStayProcess' },
    { type: 'reminder-emails', key: 'flipto.account.ReminderEmails' },
    { type: 'reengagement-emails', key: 'flipto.account.ReengagementEmails' },
    { type: 'predefinedQuote', key: 'flipto.account.platform.homepage.touchpoints.PredefinedQuote' },
    { type: 'homepage', key: 'flipto.account.platform.homepage.touchpoints.Homepage' },
    { type: 'homepageEmail', key: 'flipto.account.platform.homepage.touchpoints.EmailPage' },
    { type: 'list', key: 'flipto.account.platform.quoteList.touchpoints.List' },
    { type: 'contestPage', key: 'flipto.account.PhotoExplorer' },
    { type: 'finalistsEmail', key: 'flipto.account.sections.platform.photoAdvocacy.FinalistsEmail' },
    { type: 'reminderEmail', key: 'flipto.account.sections.platform.photoAdvocacy.ReminderEmail' },
    { type: 'activityEmail', key: 'flipto.account.sections.platform.photoAdvocacy.ActivityEmail' },
    { type: 'winnerEmail', key: 'flipto.account.sections.platform.photoAdvocacy.WinnerEmail' },
    { type: 'pre-arrival-email', key: 'flipto.account.PreArrivalEmail' },
    { type: 'poststay-email-image', key: 'flipto.account.PoststayEmailImage' },
    { type: 'photo-river', key: 'flipto.account.PhotoRiver' },
    { type: 'certificate-email', literal: 'Certificate Email' },
    { type: 'certificate-reminder-email', literal: 'Certificate Reminder Email' },
];

/**
 * Native port of the AngularJS `ftTouchpointContainer` v1-adapter — the modal chrome
 * (`<ft-modal>` shell + header + `.ft-sections-nav` tab nav) for the platform touchpoint
 * editor. Click-launched, body-mounted modal driven by the singleton TouchpointContainerService;
 * selects the native tab body (messaging/styling/integration + the two warm-lead certificate
 * editors) via `[ngSwitch]`, gated on `ready()` so a tab never mounts with an undefined payload.
 *
 * Parity invariant: the header (title + activate switch) loads ONCE on open using the initial
 * dataType; switching tabs re-fetches/re-renders the tab body only, never the header.
 * FtModalComponent is imported by deep path, never via the account-shared barrel (eval-order).
 */
@Component({
    selector: 'ft-touchpoint-container-ng',
    imports: [
        LegacyShimsModule,
        FtModalComponent,
        FtEnsureModalSingleScrollDirective,
        CapitalizePipe,
        FtTouchpointMessagingComponent,
        FtTouchpointStylingComponent,
        FtTouchpointIntegrationComponent,
        FtTouchpointCertificateEmailComponent,
        FtTouchpointCertificateReminderEmailComponent,
    ],
    encapsulation: ViewEncapsulation.None,
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    styleUrls: ['./ft-touchpoint-container.component.scss'],
    templateUrl: './ft-touchpoint-container.component.html',
})
export class FtTouchpointContainerComponent implements OnInit, OnChanges {
    readonly campaignUuid = input.required<string>();
    readonly propertyUuid = input.required<string>();
    readonly touchpointName = input.required<string>();
    /**
     * The initial dataType the modal opens on ('messaging' | 'styling' | 'integration').
     * Named `tabType` (NOT `dataType`) on purpose: a downgraded-component input bound
     * as kebab `[data-type]` collides with AngularJS's leading-`data-` attribute-prefix
     * stripping (`data-type` → `type`). `tab-type` is safe. The service still sets
     * `$ftTouchpointContainer.dataType`; the binding maps it to this input.
     */
    readonly tabType = input('messaging');

    /**
     * Close signal — emitted by the `.ft-btn-close` button and the "could not get
     * touchpoint data" error path. The launcher (NativeTouchpointContainerService)
     * subscribes and tears the mounted instance down. Using an output (rather than
     * the component calling the service's `hide()`) keeps this component free of a
     * service import that would re-enter the account-shared barrel — see
     * native-touchpoint-container.token.ts.
     */
    readonly close = output<void>();

    protected readonly supportedTabs = signal<string[]>([]);
    private readonly activeDataType = signal('messaging');
    private readonly isWarmLead = signal(false);
    /** Per-tab payload from getControllerInjectables / getWarmLeadNurturingControllerInjectables. */
    protected readonly injectables = signal<any>(null);
    /** Mirrors the v1 `$ftTHC` header model (populated once after Touchpoint.get / Platform.property). */
    protected readonly thc = signal<any>({});

    private _initialized = false;
    private _headerLoaded = false;

    // WS5 #10 Stage B B4a: the v1 factories the container grabbed via INJECTOR_TOKEN
    // (touchpointActivator/endpointUrl/Touchpoint/Platform/Messaging/Styling/Image/
    // languageHelper) are now native — `endpointUrl`/`languageHelper`/`touchpointActivator`
    // are module-level util objects (verbatim ports), the rest are injected V1Api twins.
    private readonly touchpointApi = inject(TouchpointV1ApiService);
    private readonly platformApi = inject(PlatformV1ApiService);
    private readonly messagingApi = inject(MessagingV1ApiService);
    private readonly stylingApi = inject(StylingV1ApiService);
    private readonly imageApi = inject(ImageV1ApiService);
    private readonly parse = inject(ParseService);
    private readonly zone = inject(NgZone);
    private readonly identity = inject(IdentityService);
    private readonly translateSvc = inject(TranslateService);
    /** Built once (translate at first title render), mirroring the v1 filter's construction-time table. */
    private touchPointNames: Array<{ type: string; name: string }> | null = null;

    ngOnInit(): void {
        this._initialized = true;
        this.initialize();
    }

    ngOnChanges(changes: SimpleChanges): void {
        // The TouchpointContainerService re-binds these @Inputs (without remount) when
        // `.show()` is called for a different touchpoint while the modal is still open;
        // re-initialize. ngOnChanges fires before the first ngOnInit, so guard on it.
        if (!this._initialized) return;
        if (changes['campaignUuid'] || changes['propertyUuid'] || changes['touchpointName'] || changes['tabType']) {
            this.initialize();
        }
    }

    private initialize(): void {
        this.injectables.set(null);
        this.thc.set({});
        this._headerLoaded = false;
        this.isWarmLead.set(WARM_LEAD_TOUCHPOINTS.includes(this.touchpointName()));
        this.activeDataType.set(this.tabType());
        this.supportedTabs.set(this.isWarmLead() ? [] : (SUPPORTED_TABS[this.touchpointName()] || []));

        if (this.isWarmLead()) {
            this.loadWarmLeadHeader();
            this.loadTab();
        } else {
            this.loadTab();
        }
    }

    /** Re-fetch the current tab's injectables + re-render the body (header is NOT reloaded — v1 parity). */
    private loadTab(): void {
        this.injectables.set(null);
        const dataType = this.activeDataType();

        if (this.isWarmLead()) {
            Promise.resolve(
                getWarmLeadNurturingControllerInjectables({
                    $scope: null,
                    propertyUuid: this.propertyUuid(),
                    touchpointName: this.touchpointName(),
                }),
            ).then((inj: any) => this.zone.run(() => { this.injectables.set(inj); }));
            return;
        }

        this.loadRegularInjectables(dataType)
            .then((inj: any) => this.zone.run(() => {
                this.injectables.set(inj);
                if (!this._headerLoaded) {
                    this._headerLoaded = true;
                    this.loadRegularHeader(inj);
                }
            }))
            .catch(() => this.zone.run(() => {
                alert('Could not get touchpoint data');
                this.closeContainer();
            }));
    }

    /**
     * Native port of `getControllerInjectables` (v1-adapter touchpointController.ts:8-110)
     * — the per-tab data fetch. Replaces the v1 `$injector.invoke([...])` grabs of
     * Messaging/Styling/Integration/Image/endpointUrl/languageHelper with the native
     * V1Api twins + util ports. Integration uses MessagingV1ApiService (the v1
     * `Integration` factory is the byte-identical twin of `Messaging` — both bare $http
     * aliases registered together in touchpoint.js:20-31). The native `.get` resolves the
     * BODY (v1 read `response.data`), so the unwrap drops one `.data` level vs v1.
     */
    private loadRegularInjectables(dataType: string): Promise<any> {
        const layerName = resolveLayer(this.touchpointName());
        const touchpointType = this.touchpointName();
        const touchpointGroupId = this.campaignUuid();

        switch (dataType) {
            case 'messaging': {
                const url = endpointUrl.getDataUrl(layerName, this.touchpointName(), this.campaignUuid(), this.propertyUuid());
                return this.messagingApi.get(url).then((data: any) => {
                    if (data) {
                        languageHelper.sortByDefaultLanguage(data.languages);
                        data.upgrade && languageHelper.sortByDefaultLanguage(data.upgrade.languages);
                    }
                    return { $scope: null, messaging: data, layerName, touchpointType, touchpointGroupId };
                });
            }
            case 'styling': {
                // invitation-email + reminder/reengagement + the 4 photo-advocacy emails + homepageEmail are
                // email-template pickers (no styling payload) — the native styling component loads its own
                // templates via Styling.getEmail, so skip the Styling.get/Image fetch (v1 parity).
                if (
                    touchpointType === 'invitation-email' || touchpointType === 'reminder-emails' || touchpointType === 'reengagement-emails' ||
                    touchpointType === 'finalistsEmail' || touchpointType === 'reminderEmail' || touchpointType === 'activityEmail' ||
                    touchpointType === 'winnerEmail' || touchpointType === 'homepageEmail'
                ) {
                    return Promise.resolve({ $scope: null, layerName, touchpointType, touchpointGroupId });
                }
                const styleUrl = endpointUrl.getStyleUrl(layerName, this.touchpointName(), this.campaignUuid(), this.propertyUuid());
                return Promise.all([
                    this.stylingApi.get(styleUrl),
                    this.imageApi.getPropertyImages({ propertyId: this.propertyUuid() }),
                ]).then(([styling, propertyImages]) => ({ $scope: null, styling, propertyImages, layerName, touchpointType, touchpointGroupId }));
            }
            case 'integration': {
                const url = endpointUrl.getIntegrationUrl(layerName, this.touchpointName(), this.campaignUuid(), this.propertyUuid());
                return this.messagingApi.get(url).then((data: any) => ({ $scope: null, integration: data, layerName, touchpointType, touchpointGroupId }));
            }
            default:
                return Promise.resolve(undefined);
        }
    }

    /** Port of the inline regular `$ftTouchpointHeaderController` (touchpointContainer.component.ts:74-120). */
    private loadRegularHeader(inj: any): void {
        const touchpointsWithoutIsActiveFlag = ['finalistsEmail', 'reminderEmail', 'activityEmail', 'winnerEmail', 'homepageEmail', 'followup'];
        let urlFactory: any;
        switch (this.tabType()) {
            case 'styling':
                urlFactory = touchpointsWithoutIsActiveFlag.includes(this.touchpointName()) ? endpointUrl.getDataUrl : endpointUrl.getStyleUrl;
                break;
            case 'integration':
                urlFactory = endpointUrl.getIntegrationUrl;
                break;
            default:
                urlFactory = endpointUrl.getDataUrl;
        }
        const url = urlFactory(inj.layerName, inj.touchpointType, inj.touchpointGroupId, this.propertyUuid());
        // native Touchpoint.get resolves the BODY (v1 read `response.data`).
        Promise.resolve(this.touchpointApi.get(url)).then((data: any) => this.zone.run(() => {
            let isEnabledControl = true;
            if (data.isPhotoExplorerActive !== undefined) {
                isEnabledControl = this.parse.bool(data.isPhotoExplorerActive);
            }
            this.thc.set({
                layerName: inj.layerName,
                touchpointType: inj.touchpointType,
                groupId: inj.touchpointGroupId,
                isActive: data.isActive == 1 || data.isEmailActive == 1,
                isEnabledControl,
            });
        }));
    }

    /** Port of the warm-lead `$ftTouchpointHeaderController` (touchpointContainer.component.ts:210-233). */
    private loadWarmLeadHeader(): void {
        const identity = this.identity;
        Promise.resolve(this.platformApi.property({ id: this.propertyUuid() })).then((data: any) => this.zone.run(() => {
            this.thc.set({
                touchpointType: this.touchpointName(),
                isActive: this.parse.bool(data.warmLeadNurturing.isCertificateReminderEnabled),
                isEnabledControl: identity.isFliptoStaff(),
            });
        }));
    }

    protected ready(): boolean {
        return !!this.injectables();
    }

    protected nativeTabKey(): string {
        if (this.isWarmLead()) return this.touchpointName();
        const dt = this.activeDataType();
        if (dt === 'messaging' && NATIVE_MESSAGING_TOUCHPOINTS.includes(this.touchpointName())) return 'messaging';
        if (dt === 'styling' && NATIVE_STYLING_TOUCHPOINTS.includes(this.touchpointName())) return 'styling';
        if (dt === 'integration' && NATIVE_INTEGRATION_TOUCHPOINTS.includes(this.touchpointName())) return 'integration';
        return 'v1';
    }

    protected isDataType(tab: string): boolean {
        return this.activeDataType() === tab;
    }

    protected switchDataType(tab: string): void {
        if (tab === this.activeDataType()) return;
        this.activeDataType.set(tab);
        this.loadTab();
    }

    protected touchpointTitle(): string {
        if (!this.touchpointName()) return '';
        if (!this.touchPointNames) {
            this.touchPointNames = TOUCHPOINT_NAME_ENTRIES.map((e) => ({
                type: e.type,
                name: e.literal != null ? e.literal : this.translateSvc.instant(e.key!),
            }));
        }
        const entry = this.touchPointNames.find((e) => e.type === this.touchpointName());
        return entry ? entry.name : this.touchpointName();
    }

    protected showActivate(): boolean {
        return !!this.thc().touchpointType && !!touchpointActivator.canActivate(this.thc().touchpointType);
    }

    protected updateState(): void {
        if (this.isWarmLead()) {
            this.updateWarmLeadState();
        } else {
            this.updateRegularState();
        }
    }

    private updateRegularState(): void {
        // WS5 #10 Stage B B4a: the `$rootScope.$broadcast('touchpoint.statusChanged', model)`
        // was a VERIFIED NO-OP — the only `$on('touchpoint.statusChanged')` listeners live in
        // the v1 layer controllers (messaging/styling/integration/homepage.messaging
        // .controller.js), which never instantiate at runtime (getTouchpointController has
        // zero callers ⇒ orphaned). Deleted (the 2s campaign.statusChanged precedent).
        this.touchpointApi.activate({
            propertyUuid: this.propertyUuid(),
            layer: this.thc().layerName.replace('-', ''),
            touchpoint: this.thc().groupId,
            touchpointType: this.thc().touchpointType,
            isActive: this.thc().isActive,
        });
    }

    private readonly propertyApi = inject(PropertyApiService);

    private updateWarmLeadState(): void {
        // WS5 #10 Stage B B4a: the `touchpoint.statusChanged` broadcast (incl. the v1
        // `'warn-lead-nurturing'` typo) was a verified NO-OP (orphaned listeners) → deleted.
        this.propertyApi.updateSettings(this.propertyUuid(), { isCertificateReminderEnabled: this.thc().isActive });
    }

    protected closeContainer(): void {
        // WS5 Phase 3 #3: emit to the native launcher (NativeTouchpointContainerService),
        // which mounted this component and owns its teardown. (Was
        // `$injector.get('TouchpointContainer').hide()` — the AngularJS service that
        // $compile-mounted the component; kept registered but no longer the mounter.)
        this.close.emit();
    }

    // WS5 Phase 3 #6: the v1-fallback host (`@ViewChild('v1Host')` + `mountV1Tab` +
    // `teardownV1`) was DELETED. Its only reachable tab was contestPage INTEGRATION, now
    // ported native (added to NATIVE_INTEGRATION_TOUCHPOINTS) — every SUPPORTED_TABS combo
    // is now native, so `nativeTabKey()` never returns 'v1' and the `$compile`-into-`#v1Host`
    // mount is dead. This removes the last `$compile` leaf in the touchpoint container.
}
